import React, { useState, useEffect } from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css';
import { 
  FiHome, 
  FiPackage, 

  FiShoppingCart, 
  FiMapPin, 
  FiMenu, 
  FiX, 
  FiLogOut,
  FiChevronRight,
  
  FiSettings,
  FiBell
} from 'react-icons/fi';
import { FaStore, FaProductHunt } from 'react-icons/fa';
import { MdDashboard, MdCategory } from 'react-icons/md';

function Dashboard() {
  const token = localStorage.getItem('token');
  const name = localStorage.getItem('userType');
  const userName = localStorage.getItem('userName') || 'Admin';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!token || name === 'Customer' || name === 'Salesman') {
    return <Navigate to='/login' />
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    window.location.href = '/login';
  };

  const menuItems = [
    { 
      path: 'addproduct', 
      icon: <FaProductHunt size={18} />, 
      label: 'Add Product',
      description: 'Add new products to inventory'
    },
    { 
      path: 'addcategory', 
      icon: <MdCategory size={18} />, 
      label: 'Add Category',
      description: 'Manage product categories'
    },
    { 
      path: 'orderslist', 
      icon: <FiShoppingCart size={18} />, 
      label: 'Orders List',
      description: 'View and manage orders'
    },
    { 
      path: 'shippingzone', 
      icon: <FiMapPin size={18} />, 
      label: 'Shipping Zone',
      description: 'Configure shipping zones'
    },
  ];

  const isActive = (path) => {
    return location.pathname.includes(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Top Navigation Bar */}
      <nav className="bg-white shadow-lg sticky top-0 z-30">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            
            {/* Left section - Logo & Menu Toggle */}
            <div className="flex items-center gap-4">
              {isMobile && (
                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {isSidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                </button>
              )}
              <div className="flex items-center gap-2">
                <FaStore className="text-blue-600 text-2xl" />
                <span className="font-bold text-xl text-gray-800 hidden sm:inline">
                  Admin Dashboard
                </span>
                <span className="font-bold text-xl text-gray-800 sm:hidden">
                  Dashboard
                </span>
              </div>
            </div>

            {/* Right section - User Info & Notifications */}
            <div className="flex items-center gap-4">
              {/* Notification Bell */}
              <button className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <FiBell size={20} className="text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              {/* User Dropdown */}
              <div className="relative group">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                    {userName.charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm font-semibold text-gray-800">{userName}</p>
                    <p className="text-xs text-gray-500 capitalize">{name}</p>
                  </div>
                </div>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-2">
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                    >
                      <FiLogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="flex relative">
        
        {/* Overlay for mobile sidebar */}
        {isMobile && isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-20"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed md:relative z-20 bg-gradient-to-b from-gray-900 to-gray-800 text-white
          transition-all duration-300 ease-in-out
          ${isMobile 
            ? `${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} w-72`
            : 'w-64'
          }
          h-screen overflow-y-auto shadow-xl
        `}>
          
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <MdDashboard size={24} />
              </div>
              <div>
                <h2 className="font-bold text-lg">Admin Panel</h2>
                <p className="text-xs text-gray-400 capitalize">{name}</p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="p-4">
            <div className="space-y-2">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => isMobile && setIsSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                    ${isActive(item.path)
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }
                  `}
                >
                  <span className="text-xl">{item.icon}</span>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.label}</p>
                    <p className={`text-xs ${isActive(item.path) ? 'text-blue-200' : 'text-gray-400'}`}>
                      {item.description}
                    </p>
                  </div>
                  {isActive(item.path) && <FiChevronRight size={16} />}
                </Link>
              ))}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 transition-colors text-white"
            >
              <FiLogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`
          flex-1 transition-all duration-300
          ${isMobile ? 'w-full' : 'min-h-screen'}
        `}>
          <div className="p-4 sm:p-6 lg:p-8">
            {/* Page Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <FiHome className="text-gray-400" />
                <span>Dashboard</span>
                <FiChevronRight size={14} />
                <span className="text-gray-700 capitalize">
                  {location.pathname.split('/').pop() || 'Home'}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                Welcome back, {userName}!
              </h1>
              <p className="text-gray-500 mt-1">Manage your store efficiently from here</p>
            </div>

            {/* Stats Cards (Optional) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Products</p>
                    <p className="text-2xl font-bold text-gray-800">0</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FiPackage className="text-blue-600 text-xl" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Orders</p>
                    <p className="text-2xl font-bold text-gray-800">0</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <FiShoppingCart className="text-green-600 text-xl" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Categories</p>
                    <p className="text-2xl font-bold text-gray-800">0</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <MdCategory className="text-purple-600 text-xl" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Revenue</p>
                    <p className="text-2xl font-bold text-gray-800">Rs. 0</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <FiSettings className="text-yellow-600 text-xl" />
                  </div>
                </div>
              </div>
            </div>

            {/* Outlet for nested routes */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default Dashboard